import * as React from 'react';
import { clsx } from 'clsx';
import { IconActionChevronLeft16, IconActionChevronRight16 } from '@cian/ui-kit-design-tokens/icons';

import { SlickSlider, TSlickSettings } from '../SlickSlider';
import * as styles from './ResponsiveSlider.css';

export interface IRenderDotsParams {
  currentSlide: number;
  totalSlides: number;
}

export interface IResponsiveSliderProps {
  slides: React.ReactNode[];
  renderDots?: (params: IRenderDotsParams) => React.JSX.Element;
  padding: number;
  withMargin?: boolean;
  onPrevButtonClick?(): void;
  onNextButtonClick?(): void;
}

interface IArrowProps {
  side: 'prev' | 'next';
  offset: number;
  onArrowClick?(): void;
  className?: string;
  style?: React.CSSProperties;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

/**
 * react-slick клонирует элемент стрелки, добавляя className/style/onClick и служебные currentSlide/slideCount,
 * поэтому стрелка — отдельный компонент, который пропускает на кнопку только нужное
 */
const Arrow: React.FC<React.PropsWithChildren<IArrowProps>> = ({
  side,
  offset,
  onArrowClick,
  className,
  style,
  onClick,
  children,
}) => {
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(e);
    onArrowClick?.();
  };

  return (
    <button
      type="button"
      className={clsx(styles['button'], className)}
      style={{ ...style, [side === 'prev' ? 'left' : 'right']: -offset }}
      onClick={handleClick}
    >
      {children}
    </button>
  );
};

export const ResponsiveSlider: React.FC<IResponsiveSliderProps> = ({
  slides,
  padding,
  renderDots,
  withMargin = true,
  onPrevButtonClick,
  onNextButtonClick,
}) => {
  const [currentSlide, setCurrentSlide] = React.useState(0);

  const settings = React.useMemo<TSlickSettings>(
    () => ({
      arrows: true,
      dots: false,
      infinite: false,
      slidesToShow: 1,
      slidesToScroll: 1,
      speed: 300,
      prevArrow: (
        <Arrow side="prev" offset={padding} onArrowClick={onPrevButtonClick}>
          <IconActionChevronLeft16 />
        </Arrow>
      ),
      nextArrow: (
        <Arrow side="next" offset={padding} onArrowClick={onNextButtonClick}>
          <IconActionChevronRight16 />
        </Arrow>
      ),
      beforeChange: (_current, next) => setCurrentSlide(next),
    }),
    [padding, onPrevButtonClick, onNextButtonClick],
  );

  return (
    <div className={styles['carousel-provider']}>
      <div className={styles['carousel']} style={withMargin ? { margin: `0 ${padding}px` } : undefined}>
        <SlickSlider settings={settings} className={styles['slider']}>
          {slides.map((slide, index) => (
            <div key={index} className={styles['slide']}>
              {slide}
            </div>
          ))}
        </SlickSlider>
      </div>
      {renderDots && renderDots({ currentSlide, totalSlides: slides.length })}
    </div>
  );
};
